import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const PLATFORMS = ['VRBO', 'Sandpiper', 'Kiawah Resort', 'Direct', 'Other'];

export default function BookingModal({ booking, properties, onSaved, onClose }) {
  const [propertyId, setPropertyId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [grossRevenue, setGrossRevenue] = useState('');
  const [platformFee, setPlatformFee] = useState('');
  const [platform, setPlatform] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (booking) {
      setPropertyId(booking.property_id || '');
      setGuestName(booking.guest_name || '');
      setCheckIn(booking.check_in || '');
      setCheckOut(booking.check_out || '');
      setGrossRevenue(booking.gross_revenue || '');
      setPlatformFee(booking.platform_fee || '');
      setPlatform(booking.platform || '');
      setNotes(booking.notes || '');
    } else if (properties.length === 1) {
      setPropertyId(properties[0].id);
    }
  }, [booking, properties]);

  const handleSave = async () => {
    if (!checkIn || !checkOut) { setError('Check-in and check-out dates are required.'); return; }
    if (checkOut <= checkIn) { setError('Check-out must be after check-in.'); return; }
    setLoading(true);
    setError('');

    const payload = {
      property_id: propertyId || null,
      guest_name: guestName.trim() || null,
      check_in: checkIn,
      check_out: checkOut,
      gross_revenue: grossRevenue ? parseFloat(grossRevenue) : null,
      platform_fee: platformFee ? parseFloat(platformFee) : 0,
      platform: platform || null,
      notes: notes.trim() || null,
    };

    let saved;
    if (booking) {
      const { data, error: err } = await supabase
        .from('bookings')
        .update(payload)
        .eq('id', booking.id)
        .select('*, properties(name)')
        .single();
      if (err) { setError(err.message); setLoading(false); return; }
      saved = data;
    } else {
      const { data, error: err } = await supabase
        .from('bookings')
        .insert(payload)
        .select('*, properties(name)')
        .single();
      if (err) { setError(err.message); setLoading(false); return; }
      saved = data;
    }

    setLoading(false);
    onSaved(saved, !booking);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{booking ? 'Edit Booking' : 'Add Booking'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-msg">{error}</div>}

          <div className="form-group">
            <label>Property</label>
            <select value={propertyId} onChange={e => setPropertyId(e.target.value)}>
              <option value="">-- Select --</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Guest Name</label>
            <input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="e.g. Smith Family" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Check-in *</label>
              <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Check-out *</label>
              <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Gross Revenue ($)</label>
              <input type="number" value={grossRevenue} onChange={e => setGrossRevenue(e.target.value)} placeholder="0" min="0" />
            </div>
            <div className="form-group">
              <label>Platform Fee ($)</label>
              <input type="number" value={platformFee} onChange={e => setPlatformFee(e.target.value)} placeholder="0" min="0" />
            </div>
          </div>

          <div className="form-group">
            <label>Platform</label>
            <select value={platform} onChange={e => setPlatform(e.target.value)}>
              <option value="">-- Select --</option>
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional details..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : booking ? 'Save Changes' : 'Add Booking'}
          </button>
        </div>
      </div>
    </div>
  );
}
